"use client";

/**
 * Slim FAQ tab — reuses the FAQ items from `help.helpCenter.faq` (the same
 * source as `/help`'s HelpFaqContent), but adapted for the narrow widget panel.
 * Falls back to a "browse all" link to the full help center.
 */

import { useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { ArrowRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

type FaqItem = {
  category: string;
  question: string;
  answer: string;
};

const FAQ_CATEGORY_KEYS = [
  "reservation",
  "delivery",
  "returnRefund",
  "account",
] as const;

export function SupportFaqTab() {
  const locale = useLocale();
  const tFaq = useTranslations("help.helpCenter.faq");
  const tSupport = useTranslations("support.faq");

  const items = tFaq.raw("items") as FaqItem[];

  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<"all" | string>("all");

  // Click-and-drag scrolling on the chip rail — desktop users without a
  // horizontal trackpad can drag the chips horizontally with the mouse.
  // Pointer Events covers mouse + pen + touch in a single handler.
  const chipsRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startScrollLeft: number;
    moved: boolean;
  } | null>(null);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Mouse only; let touch use native scrolling.
    if (e.pointerType !== "mouse") return;
    const el = chipsRef.current;
    if (!el) return;
    dragStateRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startScrollLeft: el.scrollLeft,
      moved: false,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const state = dragStateRef.current;
    const el = chipsRef.current;
    if (!state || !el || state.pointerId !== e.pointerId) return;
    const dx = e.clientX - state.startX;
    if (!state.moved && Math.abs(dx) > 4) {
      state.moved = true;
      // Capture once the user actually drags so chip click handlers are
      // suppressed by the click handler below.
      el.setPointerCapture(e.pointerId);
    }
    if (state.moved) {
      el.scrollLeft = state.startScrollLeft - dx;
    }
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const state = dragStateRef.current;
    if (!state || state.pointerId !== e.pointerId) return;
    const el = chipsRef.current;
    if (el && el.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
    // Keep `moved` for the synthetic click that follows pointerup.
    requestAnimationFrame(() => {
      dragStateRef.current = null;
    });
  };

  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragStateRef.current?.moved) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      const matchCategory =
        activeCategory === "all" || item.category === activeCategory;
      const matchSearch =
        !q ||
        item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q);
      return matchCategory && matchSearch;
    });
  }, [items, query, activeCategory]);

  return (
    <div className="flex flex-1 flex-col overflow-y-auto bg-gray-50">
      <div className="space-y-3 px-3 py-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tFaq("searchPlaceholder")}
            className="h-9 pl-9 text-sm"
          />
        </div>

        {/* Horizontal-scroll category chips. Negative margin extends the scroll
            track to the panel edge so it doesn't look clipped at boundaries.
            scrollbar-hide (defined in globals.css) suppresses the scrollbar UI.
            Pointer handlers enable click-and-drag scrolling for mouse users. */}
        <div
          ref={chipsRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onClickCapture={handleClickCapture}
          className="scrollbar-hide -mx-3 flex gap-1.5 overflow-x-auto px-3 pb-1 cursor-grab select-none active:cursor-grabbing"
        >
          <button
            type="button"
            onClick={() => setActiveCategory("all")}
            className={
              activeCategory === "all"
                ? "shrink-0 rounded-full bg-gray-900 px-3 py-1 text-xs font-medium text-white"
                : "shrink-0 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200"
            }
          >
            {tFaq("allCategories")}
          </button>
          {FAQ_CATEGORY_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveCategory(key)}
              className={
                activeCategory === key
                  ? "shrink-0 rounded-full bg-gray-900 px-3 py-1 text-xs font-medium text-white"
                  : "shrink-0 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200"
              }
            >
              {tFaq(`faqCategories.${key}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 px-3 pb-3">
        {filtered.length === 0 ? (
          <p className="py-6 text-center text-xs text-gray-400">
            {tFaq("noResults")}
          </p>
        ) : (
          <Accordion
            type="single"
            collapsible
            className="w-full overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)]"
          >
            {filtered.map((item, idx) => (
              <AccordionItem
                key={`${item.category}-${idx}`}
                value={`item-${idx}`}
                className="border-b border-gray-100 px-3 last:border-b-0"
              >
                <AccordionTrigger className="text-left text-sm">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-xs leading-relaxed text-gray-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>

      <div className="px-3 pt-2 pb-[calc(1rem+var(--loc-safe-area-inset-bottom))]">
        <Link
          href={`/${locale}/help`}
          className="inline-flex w-full items-center justify-between rounded-2xl bg-white px-4 py-3 text-sm font-medium text-gray-800 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)] transition hover:bg-orange-50"
        >
          <span>{tSupport("viewAll")}</span>
          <ArrowRight className="h-4 w-4 text-gray-400" />
        </Link>
      </div>
    </div>
  );
}
